/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
// libraries
import React from 'react';
import { connect } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import ReactInfiniteScroller from 'react-infinite-scroller';
import withStyles from '@material-ui/core/styles/withStyles';
import {
    object, string, shape, bool, arrayOf, func, number, array,
} from 'prop-types';
import { compose } from 'recompose';
// components
import { GraphqlSimpleProduct } from './Partials/GraphqlSimpleProduct/GraphqlSimpleProduct';
import GraphqlIcBanner from './Partials/GraphqlIcBanner';
import GraphqlCollectionSeoSchema from './Partials/GraphqlCollectionSeoSchema/GraphqlCollectionSeoSchema';
import GraphqlSeoHeadTags from '../../../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
// helpers, redux, hooks
import useSWPrefetchPDP from '../../../../../gql/hooks/useSWPrefetchPDP';
import { getFeatureFlags, getPresentationFamily } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { actions as tmActions } from '../../../../../../state/ducks/TagManager';
import { trackProductListData } from '../../../../../helpers/tracking/common/commonTrackingHelpers';
import { getUserSubmittedProductFilterZipcode, getLocationType } from '../../../../../../state/ducks/App/App-Selectors';
import { getLanguageName } from '../../../../../../state/ducks/Member/ducks/Auth/helper/helper';
import pdpDictionary from '../../../GraphqlProductPage/translations/pdpTranslations.json';

import * as app from '../../../../../../state/ducks/App';

const brandSelectors = app.ducks.brand.selectors;

const styles = (theme) => ({
    simpleProduct: {
        flex: '0 0 50%',
        maxWidth: '50%',
        marginBottom: '30px',
        position: 'relative',
        width: '100%',
        paddingRight: '10px',
        paddingLeft: '10px',
        [theme.breakpoints.up(420)]: {
            flex: '0 0 33.333333%',
            maxWidth: '33.333333%',
        },
    },
    plaSimpleProduct: {
        flex: '0 0 50%',
        maxWidth: '50%',
        marginBottom: '30px',
        position: 'relative',
        width: '100%',
        paddingRight: '10px',
        paddingLeft: '10px',
        [theme.breakpoints.up(768)]: {
            flex: '0 0 33.333333%',
            maxWidth: '33.333333%',
        },
    },
    plaSimpleProductDesktop: {
        flex: '0 0 50%',
        maxWidth: '50%',
        marginBottom: '30px',
        position: 'relative',
        width: '100%',
        paddingRight: '15px',
        paddingLeft: '15px',
        [theme.breakpoints.up(768)]: {
            flex: '0 0 33.333333%',
            maxWidth: '33.333333%',
        },
    },
    plaSingleSimpleProduct: {
        flex: '0 0 100%',
        maxWidth: '100%',
        marginBottom: '30px',
        position: 'relative',
        width: '100%',
        paddingRight: '10px',
        paddingLeft: '10px',
        [theme.breakpoints.up(768)]: {
            flex: '0 0 33.333333%',
            maxWidth: '33.333333%',
        },
    },
    dummySimpleProduct: {
        display: 'none',
        [theme.breakpoints.up(420)]: {
            display: 'block',
        },
        [theme.breakpoints.up(769)]: {
            display: 'none',
        },
    },
    productRow: {
        boxSizing: 'border-box',
        marginRight: '-10px',
        marginLeft: '-10px',
        width: 'auto',
    },
    simpleProduct4row: {
        flex: '0 0 50%',
        maxWidth: '50%',
        marginBottom: '30px',
        position: 'relative',
        width: '100%',
        paddingRight: '10px',
        paddingLeft: '10px',
        [theme.breakpoints.up(769)]: {
            flex: '0 0 25%',
            maxWidth: '25%',
        },
    },
    imocWrapper: {
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        width: '100%',
        margin: '0 auto',
        border: '0',
        alignItems: 'center',
        padding: '0 0',
        marginBottom: '10px',
        background: theme?.palette?.common?.lightPurple || '#f4f2f7',
        [theme.breakpoints.up(769)]: {
            flexDirection: 'row',
            padding: '0 0',
        },
        '& h1': {
            paddingLeft: '20px',
            margin: '10px 0 10px 10px',
            fontSize: '24px',
            color: theme?.palette?.colorPrimary,
        },
    },
});

const buildProductDataHandler = ({
    data,
    giftBox,
    localFlorist,
}) => {
    let productData = [];
    if (data) {
        productData = data;
        if (giftBox && !localFlorist) {
            productData = productData.filter((x) => (x.availability ? x.availability.productDeliveryType !== 'FPT' : null));
        }
        if (localFlorist && !giftBox) {
            productData = productData.filter((x) => (x.availability ? x.availability.productDeliveryType !== 'GPT' : null));
        }
    }
    return {
        productData,
    };
};

const GraphqlProductContainerBody = ({
    data,
    brand,
    classes,
    categoryData,
    breadCrumbArray,
    filterData: {
        giftBox,
        localFlorist,
    },
    path,
    location,
    trackEvent,
    featureFlags,
    skipAddons,
    showMovieUpsell,
    onLoadMore,
    pageNumber,
    presentation_family,
    loader,
    showLeftRailContent,
    subCollectionProducts,
    sciMainCollectionLabel,
    cmsShowSubcollection,
    mobileGridLayout,
    isSmallScreen,
}) => {
    const totalPages = categoryData?.category?.totalPages || 50;
    let productsPerRow = 3; // leave as default just in case the rest fails
    const productLayout = categoryData?.content?.entries[0]?.product_layout || [];
    let j;
    // loop thru cms data
    for (j = 0; j < productLayout.length; j += 1) {
        if (productLayout[j]?.viewport[0]?.select?.includes('Desktop')) {
            productsPerRow = productLayout[j]?.products_per_row;
        }
    }
    const showLeftRailContentFlag = brand?.code === 'PLA' ? !showLeftRailContent : showLeftRailContent;
    const blocks = categoryData?.content?.entries?.[0]?.category_blocks || categoryData?.content?.entries?.[0]?.modular_blocks || null;
    const seoContentData = categoryData?.content?.entries?.[0]?.seo || null;
    const icBannerBlock = {};
    if (blocks !== null) {
        blocks.forEach((block) => {
            if (block?.in_collection_banners?.show_after_product_number) {
                icBannerBlock[block.in_collection_banners.show_after_product_number] = block.in_collection_banners;
            }
        });
    }

    const { productData } = buildProductDataHandler({
        data,
        path,
        giftBox,
        localFlorist,
    });

    // function appends OG data to the SEO object and passes that object to GraphqlSeoHeadTags component
    const addOgDynamicData = () => {
        if (seoContentData !== null) {
            const seoCopy = { ...seoContentData };
            const { page_title, meta_description } = seoContentData;
            const name = productData && productData[0] && productData[0].image && productData[0].image.name ? productData[0].image.name : '';
            // add OPEN GRAPH DATA
            seoCopy.og = [
                { property: 'og:title', content: page_title },
                { property: 'og:type', content: 'website' },
                { property: 'og:image', content: `https://cdn1.${brand.domain}.com/wcsstore/${brand.identifier}/images/catalog/${name}x.jpg` },
                { property: 'og:description', content: meta_description },
                { property: 'og:url', content: `https://www.${brand.domain}.com${location.pathname || ''}` },
            ];
            return seoCopy;
        }
        return null;
    };

    const showProductsPerRow = () => {
        let classname = classes.simpleProduct4row;
        if (brand?.code === 'PLA') {
            classname = isSmallScreen ? classes.plaSimpleProduct : classes.plaSimpleProductDesktop;
        } else if (productsPerRow === 3 && showLeftRailContentFlag) {
            classname = classes.simpleProduct;
        }
        return classname;
    };
    // Send persist tracked products data
    if (productData.length) {
        trackProductListData({
            data,
            path,
            giftBox,
            localFlorist,
            buildProductDataHandler,
        });
    }
    // prefetch product pages of collection
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator && featureFlags && data && brand?.domain) {
        useSWPrefetchPDP(
            featureFlags,
            data,
            brand.domain,
        );
    }
    return (
        <>
            <GraphqlSeoHeadTags seoData={addOgDynamicData()} alternativeLang="true" />
            <GraphqlCollectionSeoSchema categoryData={categoryData?.content?.entries[0]?.seo} productData={productData[0]} />
            <ReactInfiniteScroller initialLoad={false} threshold={350} hasMore={(pageNumber <= totalPages) && !loader} loadMore={() => { onLoadMore(); }}>
                {subCollectionProducts && subCollectionProducts.length !== 0
                    && (
                        <div className={classes.imocWrapper}>
                            <h1>
                                {sciMainCollectionLabel}
                            </h1>
                        </div>
                    )}
                <Grid container className={classes.productRow}>
                    {/* Dummy div for tablet to move product over one position to allow position abs of GNAV */}
                    {categoryData?.guidedNavForm && brand?.code !== 'PLA' ? <div className={`${classes.simpleProduct} ${classes.dummySimpleProduct}`} /> : null}
                    {/* end Dummy div */}
                    {
                        productData.length !== 0 ? productData.map((product, i) => {
                            const {
                                name, partNumber, image, seo, skuPriceRange, availability, prices, isPassportEligible,
                            } = product;
                            const url = seo?.url || '';
                            const translatedName = getLanguageName() !== '/en' && brand?.code === 'CAN' ? pdpDictionary[`${partNumber}_name`] || name : name;
                            const showMobileProductsPerRow = () => {
                                if (brand?.code === 'PLA' && mobileGridLayout) {
                                    return classes.plaSingleSimpleProduct;
                                }
                                return showProductsPerRow();
                            };
                            if (icBannerBlock[i]) {
                                return (
                                    <>
                                        {icBannerBlock[i]?.image && (
                                            <Grid key={icBannerBlock[i]?.link?.href} item className={showProductsPerRow()}>
                                                <GraphqlIcBanner icBannerBlock={icBannerBlock[i]} presentation_family={presentation_family} />
                                            </Grid>
                                        )}
                                        <Grid item key={`${name}-${partNumber}`} className={showMobileProductsPerRow()}>
                                            {/* for the product not available flow we need to know whether the simple product component was rendered on a category page vs the homepage or any other location */}
                                            {/* to do this we check whether the product container got categoryData as opposed to some other kind of graphql data */}
                                            <GraphqlSimpleProduct
                                                partNumber={partNumber}
                                                name={translatedName}
                                                prices={prices}
                                                brand={brand}
                                                image={image}
                                                url={url}
                                                skuPriceRange={skuPriceRange}
                                                availability={availability}
                                                breadCrumbArray={breadCrumbArray}
                                                fromCategoryPage={typeof categoryData === 'object'}
                                                categoryName={categoryData?.category?.shortDescription || categoryData?.category?.name}
                                                categoryId={categoryData?.category?.id}
                                                categoryLegacyId={categoryData?.category?.legacyId}
                                                skipAddons={skipAddons || false}
                                                showMovieUpsell={showMovieUpsell || false}
                                                track
                                                trackEvent={trackEvent}
                                                featureFlags={featureFlags}
                                                isPassportEligible={isPassportEligible}
                                                isSmallScreen={isSmallScreen}
                                            />
                                        </Grid>
                                    </>
                                );
                            }
                            return (
                                <Grid item key={`${name}-${partNumber}`} className={showMobileProductsPerRow()}>
                                    {/* for the product not available flow we need to know whether the simple product component was rendered on a category page vs the homepage or any other location */}
                                    {/* to do this we check whether the product container got categoryData as opposed to some other kind of graphql data */}
                                    <GraphqlSimpleProduct
                                        partNumber={partNumber}
                                        name={translatedName}
                                        prices={prices}
                                        brand={brand}
                                        image={image}
                                        url={url}
                                        skuPriceRange={skuPriceRange}
                                        availability={availability}
                                        breadCrumbArray={breadCrumbArray}
                                        fromCategoryPage={typeof categoryData === 'object'}
                                        categoryName={categoryData?.category?.shortDescription}
                                        categoryId={categoryData?.category?.id}
                                        categoryLegacyId={categoryData?.category?.legacyId}
                                        skipAddons={skipAddons || false}
                                        showMovieUpsell={showMovieUpsell || false}
                                        track
                                        trackEvent={trackEvent}
                                        featureFlags={featureFlags}
                                        presentation_family={presentation_family}
                                        isPassportEligible={isPassportEligible}
                                        isSmallScreen={isSmallScreen}
                                    />
                                </Grid>
                            );
                        }) : null
                    }
                </Grid>
                { /* -- START: Render sub collection for sci */}
                {
                    /* eslint-disable arrow-body-style */
                    cmsShowSubcollection && subCollectionProducts && subCollectionProducts.length !== 0 ? subCollectionProducts.map((item) => {
                        return (
                            <>
                                <div className={classes.imocWrapper}>
                                    <h1>
                                        {item?.label}
                                    </h1>
                                </div>
                                <Grid container className={classes.productRow}>
                                    {
                                        item?.data && item?.data.map((product) => {
                                            const {
                                                name, partNumber, image, seo, skuPriceRange, availability, prices, isPassportEligible,
                                            } = product;
                                            const url = seo?.url || '';
                                            const translatedName = getLanguageName() !== '/en' && brand?.code === 'CAN' ? pdpDictionary[`${partNumber}_name`] || name : name;
                                            return (
                                                <Grid item key={`${name}-${partNumber}`} className={showProductsPerRow()}>
                                                    <GraphqlSimpleProduct
                                                        partNumber={partNumber}
                                                        name={translatedName}
                                                        prices={prices}
                                                        brand={brand}
                                                        image={image}
                                                        url={url}
                                                        skuPriceRange={skuPriceRange}
                                                        availability={availability}
                                                        breadCrumbArray={breadCrumbArray}
                                                        fromCategoryPage={typeof categoryData === 'object'}
                                                        categoryName={categoryData?.category?.shortDescription}
                                                        categoryId={categoryData?.category?.id}
                                                        categoryLegacyId={categoryData?.category?.legacyId}
                                                        skipAddons={skipAddons || false}
                                                        showMovieUpsell={showMovieUpsell || false}
                                                        track
                                                        trackEvent={trackEvent}
                                                        featureFlags={featureFlags}
                                                        presentation_family={presentation_family}
                                                        isPassportEligible={isPassportEligible}
                                                        isSmallScreen={isSmallScreen}
                                                    />
                                                </Grid>
                                            );
                                        })
                                    }
                                </Grid>
                            </>
                        );
                    }) : null
                }
                { /* -- END: Render sub collection for sci */}
            </ReactInfiniteScroller>
        </>
    );
};

GraphqlProductContainerBody.propTypes = {
    classes: object.isRequired,
    categoryData: object.isRequired,
    brand: object.isRequired,
    featureFlags: object.isRequired,
    breadCrumbArray: arrayOf(
        shape({
            title: string.isRequired,
            href: string.isRequired,
        }),
    ).isRequired,
    filterData: shape({
        localFlorist: bool,
        giftBox: bool,
    }).isRequired,
    path: string.isRequired,
    location: object.isRequired,
    data: arrayOf(
        shape({
            partNumber: string.isRequired,
            brand: string.isRequired,
            prices: arrayOf(
                shape({
                    __typename: string.isRequired,
                    currency: string.isRequired,
                    type: string.isRequired,
                    value: number.isRequired,
                }).isRequired,
            ).isRequired,
            skuPriceRange: shape({
                sale: arrayOf(
                    shape({
                        value: number.isRequired,
                    }),
                ),
                retail: arrayOf(
                    shape({
                        value: number.isRequired,
                    }),
                ),
            }).isRequired,
            availability: shape({
                deliveryMessage: string.isRequired,
            }).isRequired,
        }).isRequired,
    ).isRequired,
    skipAddons: bool.isRequired,
    showMovieUpsell: bool.isRequired,
    trackEvent: func.isRequired,
    onLoadMore: func.isRequired,
    pageNumber: number.isRequired,
    presentation_family: string.isRequired,
    loader: bool.isRequired,
    showLeftRailContent: bool.isRequired,
    subCollectionProducts: array.isRequired,
    sciMainCollectionLabel: string.isRequired,
    cmsShowSubcollection: bool.isRequired,
    mobileGridLayout: bool.isRequired,
    isSmallScreen: bool.isRequired,
};

const mapStateToProps = (state) => ({
    brand: brandSelectors.getBrand(state),
    locationType: getLocationType(state),
    userSubmittedZip: getUserSubmittedProductFilterZipcode(state),
    featureFlags: getFeatureFlags(state),
    presentation_family: getPresentationFamily(state),

});

const mapDispatchToProps = {
    trackEvent: tmActions.trackEvent,
};

const enhance = compose(
    withStyles(styles),
    connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(GraphqlProductContainerBody);
